import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CourseLayout from '../../components/layout/CourseLayout';
import { Button, message } from 'antd';
import LoadingBox from '../../components/loading/LoadingBox';
import { useNavigation } from '../../hooks/useNavigation';
import { TCourse } from '../../models/courses/course';
import { TLesson } from '../../models/courses/lesson';
import { useCourseApi } from '../../api/courses/courses';
import { useReferential } from '../../context/ReferentialContext';
import { CODIFICATION_LESSON_TYPE } from '../../constant/codification';
import { findEpicInCourse, findNextCoursePosition } from '../../models/courses/courseFunc';
import { useUserApi } from '../../api/user/userApp';
import { TLessonProgress } from '../../models/user/UserProgression';
import LessonContent from './LessonContent';
import { useTranslation } from 'react-i18next';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { CODIFICATION_TYPE_USERS } from '../../constant/codification';
import { saveLastPosition } from '../../utils/progress/lastPosition';
import './CourseProgress.css';
import { usePaiementApi } from '../../api/paiement/paiement';
import { useLearningHeartbeat } from '../../hooks/useLearningHeartbeat';

const CourseProgress: React.FC = () => {
    const navigate = useNavigate();
    const { lessonId } = useParams();  // Récupérer les paramètres d'URL
    const { navigateBack } = useNavigation();

    const { t } = useTranslation();
    const [canProceed, setCanProceed] = useState(false);  // Pour activer/désactiver le bouton "Suivant"
    const { getLessonDetailsById } = useCourseApi();
    const { getCodificationCodeById } = useReferential();
    const { saveProgressionUser } = useUserApi();
    const [lesson, setLesson] = useState<TLesson | null>(null);
    const [isLoading, setIsLoading] = useState(true);  // État de chargement
    const [currentLessonId, setCurrentLessonId] = useState<number>(Number(lessonId));

    const [course, setCourse] = useState<TCourse | null>(null);
    const { getCoursesDetailsByIdLesson } = useCourseApi();
    const { getProgressionUserByCourseId } = useUserApi();
    const [progression, setProgression] = useState<TLessonProgress[]>([]);
    const [isAccessible, setIsAccessible] = useState<boolean>(true);
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const { getCodeTypeUser, getTimeRemaining, getUserAppId } = useTilmiAuth();
    const paiementApi = usePaiementApi();
    const codeType = getCodeTypeUser();
    const isTeacher = codeType === CODIFICATION_TYPE_USERS.TEACHER;
    const isAdmin = codeType === CODIFICATION_TYPE_USERS.ADMIN;
    const isCourseOp = codeType === CODIFICATION_TYPE_USERS.COURSE_OP;
    const isPrivileged = isTeacher || isAdmin || isCourseOp;
    const [isPaid, setIsPaid] = useState<boolean>((getTimeRemaining() ?? -1) > 0);
    
    // Teachers can always proceed without validation
    useEffect(() => {
        if (isTeacher) {
            setCanProceed(true);
        }
    }, [isTeacher]);


    useEffect(() => {
        // Charger les détails du cours en utilisant le lessonId
        getCoursesDetailsByIdLesson(Number(lessonId))
            .then((respCourse) => {
                const courseData = respCourse.data
                setCourse(courseData);

                // Une fois le cours chargé, utiliser le courseId pour charger la progression
                return getProgressionUserByCourseId(courseData.id);
            })
            .then((respProgression) => {
                setProgression(respProgression.data || []);
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message)
                console.error("Error fetching course or progression:", err);
                navigateBack();
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line
    }, [lessonId]);

    // Vérifier l'état de facturation auprès de tc_paiement si le metadata ne donne pas d'accès
    useEffect(() => {
        let cancelled = false;
        const uid = Number(getUserAppId());
        if (!uid || isTeacher) return;
        if ((getTimeRemaining() ?? -1) > 0) return; // déjà actif via metadata
        (async () => {
            try {
                const { data } = await paiementApi.getStatuses([uid]);
                const statuses: any[] = data?.statuses || [];
                const st = statuses.find((s) => Number(s?.id_userapp) === uid);
                const exp = st?.date_expiration || st?.date_fin;
                if (!cancelled && exp) {
                    const df = new Date(exp);
                    if (!isNaN(df.getTime()) && df.getTime() > Date.now()) {
                        setIsPaid(true);
                    }
                }
            } catch {
                // ignore
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (course && progression) {
            // Vérifier si la leçon actuelle est accessible dans l'épic en cours
            const currentEpic = (Array.isArray(course?.chapters) ? course!.chapters : [])
                .filter(ch => ch && Array.isArray(ch.epics))            // 👈 ne garde que les chapitres avec epics: []
                .flatMap(ch => ch.epics!)                               // 👈 maintenant sûr
                .find(epic => Array.isArray(epic?.lessons)              // 👈 leçons : tableau
                    && epic!.lessons.some(l => l?.id === currentLessonId)
                );


            if (currentEpic) {
                const epicLessonsSource = Array.isArray(currentEpic.lessons) ? currentEpic.lessons : [];
                const epicLessons = (isPrivileged || isPaid) ? epicLessonsSource : epicLessonsSource.filter(l => l?.is_free === true);
                const currentLesson = epicLessons.find(l => l.id === currentLessonId);
                const currentLessonIndex = epicLessons.findIndex(l => l.id === currentLessonId);
                const hasAccess = currentLessonIndex <= 0 || epicLessons.slice(0, currentLessonIndex).every(l =>
                    progression.some(p => p.lesson_id === l.id)
                );

                const paidGate = (isPrivileged || isPaid || currentLesson?.is_free === true);
                setIsAccessible(isPrivileged ? true : (paidGate && hasAccess));
            } else {
                // Si l'épic contenant la leçon n'est pas trouvé, on considère que le cours n'est pas accessible
                setIsAccessible(false);
            }
            // Mettre à jour l'état indiquant que la vérification est terminée
            setIsCheckingAccess(false);
        }

    }, [course, progression, currentLessonId])


    // Position reelle de l'eleve : on la memorise a chaque changement de lecon
    // pour pouvoir y revenir au lieu de repartir de l'introduction du cours.
    useEffect(() => {
        if (course?.id && currentLessonId > 0) {
            saveLastPosition(getUserAppId(), course.id, currentLessonId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course?.id, currentLessonId]);

    // Nouveaux états pour gérer les leçons et l'indice actuel
    const [lessons, setLessons] = useState<TLesson[]>([]);
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

    useEffect(() => {
        if (currentLessonId > 0) {
            setIsLoading(true);
            getLessonDetailsById(currentLessonId)
                .then((response) => {
                    let loaded = response.data as TLesson;
                    const cfg = loaded?.config?.config as any;
                    const hasDataset = cfg && typeof cfg === 'object' && ('dataset' in cfg);
                    if (!hasDataset && course) {
                        const fromCourse = (findEpicInCourse(course, loaded.epic_id)?.lessons || []).find(l => l?.id === loaded.id);
                        if (fromCourse?.config?.config) {
                            loaded = { ...loaded, config: fromCourse.config };
                        }
                    }
                    setLesson(loaded);
                })
                .catch((err) => {
                    if (course) {
                        const fromCourse = (course.chapters || [])
                            .flatMap(ch => ch?.epics || [])
                            .flatMap(ep => ep?.lessons || [])
                            .find(l => l?.id === currentLessonId);
                        if (fromCourse) {
                            setLesson(fromCourse);
                            setIsLoading(false);
                            return;
                        }
                    }
                    message.error(err.response?.data ?? err.message ?? 'Failed to load lesson');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [currentLessonId, course]);

    useEffect(() => {
        if (lesson && course) {
            const epicLessonsSource = findEpicInCourse(course, lesson.epic_id)?.lessons || [];
            const epicLessons = (isPrivileged || isPaid) ? epicLessonsSource : epicLessonsSource.filter(l => l?.is_free === true);
            setLessons(epicLessons);

            const index = epicLessons.findIndex(l => l.id === lesson.id);
            setCurrentStepIndex(index);
            setCanProceed(false); // Réinitialise canProceed lors du changement de leçon
        }
    }, [lesson, course, isTeacher, isPaid]);

    // Assurer l'activation de "Suivant" pour les leçons de type SLIDE ou déjà complétées
    useEffect(() => {
        if (lesson) {
            const typeCode = getCodificationCodeById(lesson.lesson_type_id);
            const isAlreadyCompleted = progression.some(p => p.lesson_id === lesson.id);
            if (typeCode === CODIFICATION_LESSON_TYPE.LESSON_SLIDE || isPrivileged || isAlreadyCompleted) {
                setCanProceed(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lesson, getCodificationCodeById, isPrivileged, progression]);

    const saveProgression = async () => {
        if (!lesson) return;

        setIsSaving(true);

        try {
            const response = await saveProgressionUser(lesson.id); // Assurez-vous que saveProgressionUser est bien une Promise
            setProgression((prev) => [
                ...prev,
                { id: 0, userapp_id: 0, lesson_id: lesson.id },
            ]);
            return response; // Retourner la réponse pour indiquer la fin de la tâche
        } catch (err: any) {
            message.error(err.response?.data ?? err.message ?? err);
            throw err; // Propager l'erreur si nécessaire
        } finally {
            setIsSaving(false);
        }
    };

    const next = async () => {
        await saveProgression();
        if (currentStepIndex + 1 < lessons.length) {
            goToStep(currentStepIndex + 1);
        }
    };


    const previous = () => {
        if (currentStepIndex > 0) {
            goToStep(currentStepIndex - 1);
        }
    };

    const goToStep = (stepIndex: number) => {
        const targetLesson = lessons[stepIndex];
        if (targetLesson) {
            setCurrentLessonId(targetLesson.id)
        }
    };

    const finishCourse = async () => {
        await saveProgression();
        if (!course) {
            navigate(`/program`);
            return;
        }

        // La derniere lecon d'une epopee n'est pas la fin du cours : on resout la
        // lecon suivante a travers les frontieres d'epopee et de chapitre. Sans
        // cela la page de detail recalculait la position a partir d'une
        // progression encore incomplete et rouvrait le premier chapitre.
        const nextPosition = findNextCoursePosition(course, currentLessonId);

        if (!nextPosition) {
            // Cours termine : plus rien a ouvrir, on revient sur le detail.
            navigate(`/course-details/${course.id}?resume=1`);
            return;
        }

        // On memorise la position cible avant de naviguer pour que la page de
        // detail ouvre la bonne section meme si la progression serveur n'est pas
        // encore relue.
        saveLastPosition(getUserAppId(), course.id, nextPosition.lesson.id);

        navigate(
            `/course-details/${course.id}?resume=1&lesson=${nextPosition.lesson.id}` +
            `&chapter=${nextPosition.chapter.id}&epic=${nextPosition.epic.id}`
        );
    };


    const checkResult = useCallback((isValid: boolean) => {
        // Teachers can always proceed, regardless of validation
        setCanProceed(isTeacher ? true : isValid);
    }, [isTeacher]);


    // Signal de présence (temps actif, D-13 provisoire) : seulement quand la
    // leçon est réellement affichée — ni pendant le chargement, ni sur l'écran
    // « Leçon non accessible ».
    const isLessonOpen = !isLoading && !isCheckingAccess && isAccessible && !!lesson;
    useLearningHeartbeat(isLessonOpen ? lesson!.id : null);

    const currentChapter = useMemo(() => {
        if (!course || !lesson) return null;
        return (course.chapters ?? []).find((ch) =>
            ch?.epics?.some((epic) => epic?.id === lesson.epic_id)
        );
    }, [course, lesson]);

    const currentEpic = useMemo(() => {
        return currentChapter?.epics?.find((epic) => epic?.id === lesson?.epic_id) ?? null;
    }, [currentChapter, lesson]);

    return (
        <CourseLayout
            title={t('courses.progress.pageTitle', 'Course progression')}
            lessons={lessons}
            progression={progression}
            currentStep={currentStepIndex}
            previous={currentStepIndex > 0 ? previous : undefined}
            next={currentStepIndex + 1 < lessons.length ? (isPrivileged ? next : (canProceed ? next : undefined)) : undefined}
            finishCourse={currentStepIndex + 1 === lessons.length ? (isPrivileged ? finishCourse : (canProceed ? finishCourse : undefined)) : undefined}
            goToStep={goToStep}
            isLoading={isLoading}
            isSaving={isSaving}
            onBackNavigation={navigateBack}
            showBackButton={true}
        >
            {isLoading || isCheckingAccess ? (
                <LoadingBox />
            ) : !isAccessible ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h3>{'Leçon non accessible'}</h3>
                    <p>
                        {isTeacher ? null : (
                            isPaid ?
                                'Vous devez compléter les leçons précédentes pour accéder à cette leçon.' :
                                'Cette leçon est réservée aux comptes actifs. Veuillez activer votre abonnement pour y accéder.'
                        )}
                    </p>
                    <Button type="primary" onClick={() => navigate(`/program`)}>Retour au cours</Button>
                </div>
            ) : (
                <>
                    <section className="course-progress-hero">
                        <div className="course-progress-hero__badge">
                            {currentChapter?.name || t('courses.progress.unknownChapter', 'Chapter')}
                            {currentEpic?.name ? ` • ${currentEpic.name}` : ''}
                        </div>
                        <div className="course-progress-hero__header">
                            <h1>{lesson?.name}</h1>
                        </div>
                    </section>

                    <div className="course-progress-content">
                        <LessonContent
                            key={lesson?.id || 0}
                            lesson={lesson}
                            checkResult={checkResult}
                        />
                    </div>
                </>
            )}
        </CourseLayout>
    );
};

export default CourseProgress;
